import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import {
    UserAddOutlined,
    EditOutlined,
    EyeOutlined,
    UserOutlined,
    CalendarOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { MdClose } from 'react-icons/md';
import { FIELD_LABEL, FIELD_ERROR, REQUIRED_MARK, fieldControl } from './formStyles';
import dayjs from 'dayjs';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import Loader from '../../components/Loader/Loader';
import CommonPagination from '../../components/CommonPagination/CommonPagination';
import '../../components/Popup/Popup.scss';
import {
    UserSummaryDTO,
    UserDetailsDTO,
    CreateUserRequest,
    UpdateUserRequest,
    RoleDTO,
    UserFormValues,
    UserListFilters,
    ApiResponse,
    ResponseModel
} from './types';

interface UsersManagementProps {
    roles: RoleDTO[];
}

// @@ AI-COMMENT: Rows per page for the users listing
const USERS_PAGE_SIZE = 25;

// @@ AI-COMMENT: Blank form state shared by the create and edit dialogs
const EMPTY_USER_FORM: UserFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    status: true,
    roleIds: []
};

// @@ AI-COMMENT: Sliding toggle replacing the antd Switch for the Active/Inactive status
const StatusToggle: React.FC<{ checked: boolean; onChange: (next: boolean) => void }> = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className="flex items-center gap-2.5 group"
    >
        <span
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-blue-500 group-hover:bg-blue-600' : 'bg-[#C9C9D2] group-hover:bg-[#B5B5C0]'}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
            />
        </span>
        <span className={`text-xs font-semibold ${checked ? 'text-green-600' : 'text-[#959595]'}`}>
            {checked ? 'Active' : 'Inactive'}
        </span>
    </button>
);

// @@ AI-COMMENT: Searchable checkbox list replacing the antd multi-select for role assignment.
// Deliberately kept outside .form-field so forms.scss does not style the checkboxes as text inputs.
const RolePicker: React.FC<{
    roles: RoleDTO[];
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    invalid?: boolean;
}> = ({ roles, selectedIds, onChange, invalid }) => {
    const [search, setSearch] = useState('');
    const filtered = roles.filter(role => role.roleName.toLowerCase().includes(search.toLowerCase()));
    const selectedRoles = roles.filter(role => selectedIds.includes(role.id));

    const toggle = (id: number) => {
        onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
    };

    return (
        <div className={`border rounded-md bg-white overflow-hidden transition-colors ${invalid ? 'border-red-400' : 'border-[#E0E0E6]'}`}>
            <div className="p-2 border-b border-[#EFEFF3] flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <SearchOutlined className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A3A6B4] text-[11px]" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search roles"
                        className="w-full h-8 border border-[#E0E0E6] rounded-md pl-7 pr-2 text-xs outline-none transition-colors placeholder:text-[#A3A6B4] hover:border-[#C9C9D2] focus:border-[#278BED] focus:ring-2 focus:ring-blue-100"
                    />
                </div>
                {selectedIds.length > 0 && (
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        className="text-[11px] font-medium text-[#278BED] hover:underline whitespace-nowrap"
                    >
                        Clear all
                    </button>
                )}
            </div>

            {/* @@ AI-COMMENT: Selected roles surface as chips so they stay visible without scrolling the list */}
            {selectedRoles.length > 0 && (
                <div className="px-2 py-2 border-b border-[#EFEFF3] bg-[#FAFAFC] flex flex-wrap gap-1">
                    {selectedRoles.map(role => (
                        <span
                            key={role.id}
                            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold max-w-full"
                        >
                            <span className="truncate">{role.roleName}</span>
                            <button
                                type="button"
                                title={`Remove ${role.roleName}`}
                                onClick={() => toggle(role.id)}
                                className="flex-shrink-0 rounded-full hover:bg-blue-200 p-0.5"
                            >
                                <MdClose size={10} />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="max-h-[132px] overflow-y-auto">
                {filtered.length === 0 ? (
                    <p className="px-2.5 py-4 text-xs text-[#A3A6B4] italic text-center">No roles match "{search}"</p>
                ) : (
                    filtered.map(role => {
                        const isSelected = selectedIds.includes(role.id);
                        return (
                            <label
                                key={role.id}
                                title={role.roleDescription}
                                className={`flex items-center gap-2.5 px-2.5 py-1.5 cursor-pointer transition-colors ${isSelected ? 'bg-[#F0F7FF]' : 'hover:bg-[#FAFAFC]'}`}
                            >
                                <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 flex-shrink-0 cursor-pointer accent-[#278BED]"
                                    checked={isSelected}
                                    onChange={() => toggle(role.id)}
                                />
                                <span className={`text-xs truncate ${isSelected ? 'text-[#278BED] font-semibold' : 'text-[#3F3F3F]'}`}>
                                    {role.roleName}
                                </span>
                            </label>
                        );
                    })
                )}
            </div>
        </div>
    );
};

const UsersManagement: React.FC<UsersManagementProps> = ({ roles }) => {
    // Get logged-in user from authStore
    const { user } = useSelector((s: any) => s.authStore);
    // @@ AI-COMMENT: State management
    const [users, setUsers] = useState<UserSummaryDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = USERS_PAGE_SIZE;

    // @@ AI-COMMENT: Filter and search state
    const [filters, setFilters] = useState<UserListFilters>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);
    const [roleFilter, setRoleFilter] = useState<number | undefined>(undefined);

    // @@ AI-COMMENT: Modal state management
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserDetailsDTO | null>(null);
    const [modalLoading, setModalLoading] = useState(false);

    // @@ AI-COMMENT: Controlled form state and validation errors, replacing the antd Form instances
    const [userForm, setUserForm] = useState<UserFormValues>(EMPTY_USER_FORM);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const setField = (key: keyof UserFormValues, value: any) => {
        setUserForm(prev => ({ ...prev, [key]: value }));
    };

    // @@ AI-COMMENT: Mirrors the validation rules the antd Form used to enforce
    const validateUserForm = () => {
        const errors: Record<string, string> = {};
        if (!userForm.firstName.trim()) errors.firstName = 'Please enter first name';
        if (!userForm.lastName.trim()) errors.lastName = 'Please enter last name';
        if (!userForm.email.trim()) {
            errors.email = 'Please enter email';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email.trim())) {
            errors.email = 'Please enter a valid email';
        }
        if (!userForm.roleIds.length) errors.roleIds = 'Please select at least one role';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const closeCreateModal = () => {
        setCreateModalVisible(false);
        setUserForm(EMPTY_USER_FORM);
        setFormErrors({});
    };

    const closeEditModal = () => {
        setEditModalVisible(false);
        setSelectedUser(null);
        setUserForm(EMPTY_USER_FORM);
        setFormErrors({});
    };

    // @@ AI-COMMENT: Fetch users with filters and pagination
    const fetchUsers = async (page = currentPage, size = pageSize, customFilters?: UserListFilters) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', (page - 1).toString()); // API uses 0-based indexing
            params.append('size', size.toString());

            const activeFilters = customFilters || filters;
            if (activeFilters.searchTerm?.trim()) {
                params.append('searchTerm', activeFilters.searchTerm.trim());
            }
            if (activeFilters.status !== undefined) {
                params.append('status', activeFilters.status.toString());
            }
            if (activeFilters.roleId) {
                params.append('roleId', activeFilters.roleId.toString());
            }
            params.append('sort', 'createdAt,desc');

            const response = await axiosInstance.get<ApiResponse<UserSummaryDTO[]>>(
                `${apis.USER_MANAGEMENT_LIST}?${params.toString()}`
            );

            if (response.data?.status && response.data.data) {
                setUsers(response.data.data);
                setTotalCount(response.data.totalCount);
            } else {
                setUsers([]);
                message.error('Failed to fetch users');
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            message.error(error?.response?.data?.description || 'Failed to fetch users');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // @@ AI-COMMENT: Fetch user details for view/edit
    const fetchUserDetails = async (userId: number) => {
        setModalLoading(true);
        try {
            const response = await axiosInstance.get<ResponseModel<UserDetailsDTO>>(
                `${apis.USER_MANAGEMENT_GET_USER}?userId=${userId}`
            );

            if (response.data?.status && response.data.data) {
                return response.data.data;
            } else {
                message.error('User not found');
                return null;
            }
        } catch (error: any) {
            console.error('Error fetching user details:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to fetch user details');
            return null;
        } finally {
            setModalLoading(false);
        }
    };

    // @@ AI-COMMENT: Create user
    const handleCreateUser = async (values: UserFormValues) => {
        setModalLoading(true);
        try {
            const payload: CreateUserRequest = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                status: values.status,
                roleIds: values.roleIds
            };

            const response = await axiosInstance.post<ResponseModel<UserDetailsDTO>>(
                apis.USER_MANAGEMENT_CREATE_USER,
                payload,
                {
                    headers: {
                        userId: user?.id?.toString() || ''
                    }
                }
            );

            if (response.data?.status) {
                message.success('User created successfully');
                closeCreateModal();
                fetchUsers();
            } else {
                message.error(response.data?.statusDescription || 'Failed to create user');
            }
        } catch (error: any) {
            console.error('Error creating user:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to create user');
        } finally {
            setModalLoading(false);
        }
    };

    // @@ AI-COMMENT: Update user
    const handleUpdateUser = async (values: UserFormValues) => {
        if (!selectedUser) {
            message.error('No user selected for update');
            return;
        }

        setModalLoading(true);
        try {
            const payload: UpdateUserRequest = {
                userId: selectedUser.id,
                firstName: values.firstName !== selectedUser.firstName ? values.firstName : undefined,
                lastName: values.lastName !== selectedUser.lastName ? values.lastName : undefined,
                email: values.email !== selectedUser.email ? values.email : undefined,
                status: values.status !== selectedUser.status ? values.status : undefined,
                roleIds: values.roleIds
            };

            const response = await axiosInstance.post<ResponseModel<UserDetailsDTO>>(
                apis.USER_MANAGEMENT_UPDATE_USER,
                payload,
                {
                    headers: {
                        userId: user?.id?.toString() || ''
                    }
                }
            );

            if (response.data?.status) {
                message.success('User updated successfully');
                closeEditModal();
                fetchUsers();
            } else {
                message.error(response.data?.statusDescription || 'Failed to update user');
            }
        } catch (error: any) {
            console.error('Error updating user:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to update user');
        } finally {
            setModalLoading(false);
        }
    };



    // @@ AI-COMMENT: Handle search and filters
    const handleSearch = () => {
        const newFilters: UserListFilters = {
            searchTerm,
            status: statusFilter,
            roleId: roleFilter
        };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchUsers(1, pageSize, newFilters);
    };

    const handleReset = () => {
        setSearchTerm('');
        setStatusFilter(undefined);
        setRoleFilter(undefined);
        setFilters({});
        setCurrentPage(1);
        fetchUsers(1, pageSize, {});
    };

    // @@ AI-COMMENT: Modal handlers
    const handleViewUser = async (userId: number) => {
        const userDetails = await fetchUserDetails(userId);
        if (userDetails) {
            setSelectedUser(userDetails);
            setViewModalVisible(true);
        }
    };

    const handleEditUser = async (userId: number) => {
        const userDetails = await fetchUserDetails(userId);
        if (userDetails) {
            setSelectedUser(userDetails);
            setUserForm({
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                email: userDetails.email,
                status: userDetails.status,
                roleIds: userDetails.roles.map(role => role.id)
            });
            setFormErrors({});
            setEditModalVisible(true);
        }
    };

    // @@ AI-COMMENT: Submit guards run validation before hitting the create/update handlers
    const submitCreateUser = () => {
        if (validateUserForm()) handleCreateUser(userForm);
    };

    const submitUpdateUser = () => {
        if (validateUserForm()) handleUpdateUser(userForm);
    };

    // @@ AI-COMMENT: Pagination handlers for the shared CMS pager and the rows-per-page selector
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchUsers(page, pageSize);
    };


    // @@ AI-COMMENT: Initial data load
    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            {/* @@ AI-COMMENT: Filter strip matching the shared CMS listing filter bar */}
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[240px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Search</label>
                    <input
                        type="text"
                        placeholder="Search by name or email"
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                </div>
                <div className="w-[140px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Status</label>
                    <select
                        className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
                        value={statusFilter === undefined ? '' : String(statusFilter)}
                        onChange={e => setStatusFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
                <div className="w-[180px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Role</label>
                    <select
                        className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
                        value={roleFilter === undefined ? '' : String(roleFilter)}
                        onChange={e => setRoleFilter(e.target.value === '' ? undefined : Number(e.target.value))}
                    >
                        <option value="">All Roles</option>
                        {roles.map(role => (
                            <option key={role.id} value={role.id}>{role.roleName}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={handleSearch}
                    className="h-8 px-5 rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
                >
                    Search
                </button>
                <button
                    onClick={handleReset}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                    Reset
                </button>
                <button
                    onClick={() => setCreateModalVisible(true)}
                    className="h-8 px-4 ml-auto rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 flex items-center gap-1.5"
                >
                    <UserAddOutlined />
                    Add User
                </button>
            </div>

            {/* @@ AI-COMMENT: Users table using the shared CMS table chrome (dark head, zebra rows) */}
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]">
                        <thead className="bg-[#343434]">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[18%]">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[22%]">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[32%]">Roles</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-white w-[10%]">Status</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-white w-[12%]">Created</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-white w-[6%]">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-500">
                                        <Loader />
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-500">
                                        No users found.
                                    </td>
                                </tr>
                            ) : (
                                users.map(record => (
                                    <tr
                                        key={record.id}
                                        className="[&:nth-child(odd)]:bg-[#f7f7f7] hover:bg-[#eef4fb] cursor-pointer"
                                        onClick={() => handleViewUser(record.id)}
                                    >
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">
                                            <span className="flex items-center gap-2">
                                                <span className="w-7 h-7 rounded-full bg-[#E5EEFC] flex items-center justify-center flex-shrink-0">
                                                    <UserOutlined style={{ color: '#1976d2', fontSize: 12 }} />
                                                </span>
                                                <span className="font-semibold">{record.firstName} {record.lastName}</span>
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] truncate max-w-[220px]" title={record.email}>
                                            {record.email}
                                        </td>
                                        <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">
                                            {record.roles && record.roles.length > 0 ? (
                                                <span className="flex flex-wrap gap-1">
                                                    {record.roles.map((role: any) => (
                                                        <span
                                                            key={role.id}
                                                            className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold"
                                                        >
                                                            {role.roleName || role.name}
                                                        </span>
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-gray-400">No roles</span>
                                            )}
                                        </td>
                                        {/* @@ AI-COMMENT: Status rendered as a pill, matching the pill treatment on other CMS listings */}
                                        <td className="px-4 py-3 text-center text-xs">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${record.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {record.status ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center text-xs text-[#3F3F3F]">
                                            <span className="inline-flex items-center gap-1">
                                                <CalendarOutlined style={{ color: '#9ca3af', fontSize: 10 }} />
                                                {dayjs(record.createdAt).format('DD MMM YYYY')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center text-xs">
                                            <span className="inline-flex items-center justify-center gap-1">
                                                <button
                                                    title="View details"
                                                    onClick={e => { e.stopPropagation(); handleViewUser(record.id); }}
                                                    className="p-1.5 rounded-sm text-green-600 hover:bg-green-50"
                                                >
                                                    <EyeOutlined />
                                                </button>
                                                <button
                                                    title="Edit user"
                                                    onClick={e => { e.stopPropagation(); handleEditUser(record.id); }}
                                                    className="p-1.5 rounded-sm text-blue-600 hover:bg-blue-50"
                                                >
                                                    <EditOutlined />
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* @@ AI-COMMENT: Shared CMS pagination replaces the antd pager for consistency across listings */}
            {totalCount > 0 && (
                <CommonPagination
                    data={{ totalCount }}
                    PAGE_SIZE={pageSize}
                    onPageChange={handlePageChange}
                />
            )}


            {/* @@ AI-COMMENT: Create / Edit User dialog using the shared CMS popup shell */}
            {(createModalVisible || editModalVisible) && (
                <div className="popup-container">
                    <div className="overlay">
                        <div className="popup-content popup-card popup-card-wide">
                            <div className="popup-head flex items-start justify-between">
                                <div>
                                    <h2>{createModalVisible ? 'Create New User' : 'Edit User'}</h2>
                                    <p>{createModalVisible ? 'Fill in the below details.' : 'Update the details for this user.'}</p>
                                </div>
                                <button
                                    onClick={createModalVisible ? closeCreateModal : closeEditModal}
                                    title="Close"
                                    className="popup-close flex items-center justify-center"
                                >
                                    <MdClose size={18} />
                                </button>
                            </div>

                            <div className="popup-body space-y-4">
                                <div className="flex gap-3">
                                    <div className="flex-1 min-w-0">
                                        <label className={FIELD_LABEL}>First Name <span className={REQUIRED_MARK}>*</span></label>
                                        <input
                                            type="text"
                                            placeholder="Enter first name"
                                            className={fieldControl(Boolean(formErrors.firstName))}
                                            value={userForm.firstName}
                                            onChange={e => setField('firstName', e.target.value)}
                                        />
                                        {formErrors.firstName && <p className={FIELD_ERROR}>{formErrors.firstName}</p>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <label className={FIELD_LABEL}>Last Name <span className={REQUIRED_MARK}>*</span></label>
                                        <input
                                            type="text"
                                            placeholder="Enter last name"
                                            className={fieldControl(Boolean(formErrors.lastName))}
                                            value={userForm.lastName}
                                            onChange={e => setField('lastName', e.target.value)}
                                        />
                                        {formErrors.lastName && <p className={FIELD_ERROR}>{formErrors.lastName}</p>}
                                    </div>
                                </div>
                                <div>
                                    <label className={FIELD_LABEL}>Email <span className={REQUIRED_MARK}>*</span></label>
                                    <input
                                        type="email"
                                        placeholder="Enter email address"
                                        className={fieldControl(Boolean(formErrors.email))}
                                        value={userForm.email}
                                        onChange={e => setField('email', e.target.value)}
                                    />
                                    {formErrors.email && <p className={FIELD_ERROR}>{formErrors.email}</p>}
                                </div>
                                <div>
                                    <label className={FIELD_LABEL}>Roles <span className={REQUIRED_MARK}>*</span></label>
                                    <RolePicker
                                        roles={roles}
                                        selectedIds={userForm.roleIds}
                                        onChange={ids => setField('roleIds', ids)}
                                        invalid={Boolean(formErrors.roleIds)}
                                    />
                                    {formErrors.roleIds && <p className={FIELD_ERROR}>{formErrors.roleIds}</p>}
                                </div>
                                <div className="flex items-center justify-between rounded-md border border-[#E0E0E6] bg-[#FAFAFC] px-3 py-2.5">
                                    <div>
                                        <p className="text-[11px] font-semibold text-[#43425D]">Status</p>
                                        <p className="text-[10px] text-[#959595] mt-0.5">Inactive users cannot sign in.</p>
                                    </div>
                                    <StatusToggle checked={userForm.status} onChange={next => setField('status', next)} />
                                </div>
                            </div>

                            <div className="popup-foot bottom-btn-cta flex justify-end items-center">
                                <button
                                    onClick={createModalVisible ? closeCreateModal : closeEditModal}
                                    className="bg-transparent font-semibold text-slate-800 py-2 px-6"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={createModalVisible ? submitCreateUser : submitUpdateUser}
                                    disabled={modalLoading}
                                    className="bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 py-2 px-6"
                                >
                                    {modalLoading
                                        ? 'Saving...'
                                        : createModalVisible ? 'Create User' : 'Update User'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* @@ AI-COMMENT: View User dialog using the shared CMS popup shell */}
            {viewModalVisible && selectedUser && (
                <div className="popup-container">
                    <div className="overlay">
                        <div className="popup-content popup-card popup-card-wide">
                            <div className="popup-head flex items-start justify-between">
                                <div>
                                    <h2>User Details</h2>
                                    <p>Read-only view of this user's profile.</p>
                                </div>
                                <button
                                    onClick={() => { setViewModalVisible(false); setSelectedUser(null); }}
                                    title="Close"
                                    className="popup-close flex items-center justify-center"
                                >
                                    <MdClose size={18} />
                                </button>
                            </div>

                            <div className="popup-body">
                                <div className="flex items-center gap-4 pb-4 border-b border-[#ededed]">
                                    <span className="w-12 h-12 rounded-full bg-[#E5EEFC] flex items-center justify-center flex-shrink-0">
                                        <UserOutlined style={{ color: '#1976d2', fontSize: 20 }} />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-[#43425D] truncate">
                                            {selectedUser.firstName} {selectedUser.lastName}
                                        </p>
                                        <p className="text-xs text-[#959595] truncate">{selectedUser.email}</p>
                                    </div>
                                    <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${selectedUser.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {selectedUser.status ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div className="flex gap-4 mt-4">
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-[#303030] mb-[5px]">Created At</p>
                                        <p className="text-xs text-[#3F3F3F]">{dayjs(selectedUser.createdAt).format('DD MMM YYYY, HH:mm')}</p>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-[#303030] mb-[5px]">Updated At</p>
                                        <p className="text-xs text-[#3F3F3F]">{dayjs(selectedUser.updatedAt).format('DD MMM YYYY, HH:mm')}</p>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="text-[10px] font-bold text-[#303030] mb-[5px]">Assigned Roles</p>
                                    {selectedUser.roles.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {selectedUser.roles.map(role => (
                                                <span
                                                    key={role.id}
                                                    title={role.roleDescription}
                                                    className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-semibold"
                                                >
                                                    {role.roleName}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-400 italic">No roles assigned</p>
                                    )}
                                </div>
                            </div>

                            <div className="popup-foot bottom-btn-cta flex justify-end items-center">
                                <button
                                    onClick={() => { setViewModalVisible(false); setSelectedUser(null); }}
                                    className="bg-transparent font-semibold text-slate-800 py-2 px-6"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => { setViewModalVisible(false); handleEditUser(selectedUser.id); }}
                                    className="bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 py-2 px-6"
                                >
                                    Edit User
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};


export default UsersManagement;
